import type {
  AlertState,
  AlertView,
  ConnectorStatus,
  Detection,
  Diagnosis,
  Severity,
} from '../enums.js';

// Maps spec states to what the prototype shows (plan Appendix B, ADR 0001 #1). The UI
// takes every label and pill class from here or from the API, never from its own code.

/** The prototype's six status keys, plus the amber "Needs decision" the spec adds. */
export type UiStatusKey =
  'new' | 'draft' | 'waiting' | 'verifying' | 'closed' | 'accepted' | 'needs_decision';

export interface AlertStateUi {
  /** Pill text, copied from the prototype's STATUS map where it has one. */
  readonly label: string;
  /** Prototype pill class (`.pill.p-*`). */
  readonly pillClass: 'p-new' | 'p-draft' | 'p-waiting' | 'p-verifying' | 'p-closed' | 'p-accepted';
  /** Which status icon (`SI`) to draw. */
  readonly icon: Exclude<UiStatusKey, 'needs_decision'>;
  readonly statusKey: UiStatusKey;
  /** Alerts list "View" bucket. */
  readonly view: Exclude<AlertView, 'all'>;
}

export const ALERT_STATE_UI = {
  triaged: { label: 'New', pillClass: 'p-new', icon: 'new', statusKey: 'new', view: 'action' },
  reopened: { label: 'New', pillClass: 'p-new', icon: 'new', statusKey: 'new', view: 'action' },
  draft_ready: {
    label: 'Draft ready',
    pillClass: 'p-draft',
    icon: 'draft',
    statusKey: 'draft',
    view: 'action',
  },
  needs_human_decision: {
    label: 'Needs decision',
    pillClass: 'p-new',
    icon: 'new',
    statusKey: 'needs_decision',
    view: 'action',
  },
  awaiting_others: {
    label: 'Waiting for reply',
    pillClass: 'p-waiting',
    icon: 'waiting',
    statusKey: 'waiting',
    view: 'waiting',
  },
  awaiting_verification: {
    label: 'Verifying',
    pillClass: 'p-verifying',
    icon: 'verifying',
    statusKey: 'verifying',
    view: 'waiting',
  },
  closed_verified: {
    label: 'Closed',
    pillClass: 'p-closed',
    icon: 'closed',
    statusKey: 'closed',
    view: 'closed',
  },
  closed_human: {
    label: 'Closed',
    pillClass: 'p-closed',
    icon: 'closed',
    statusKey: 'closed',
    view: 'closed',
  },
  accepted_deviation: {
    label: 'Accepted as intentional',
    pillClass: 'p-accepted',
    icon: 'accepted',
    statusKey: 'accepted',
    view: 'closed',
  },
  superseded: {
    label: 'Superseded',
    pillClass: 'p-accepted',
    icon: 'accepted',
    statusKey: 'accepted',
    view: 'closed',
  },
} as const satisfies Record<AlertState, AlertStateUi>;

export function alertStateUi(state: AlertState): AlertStateUi {
  return ALERT_STATE_UI[state];
}

/** Alert states in each Alerts list view. `all` is every state. */
export function statesInView(view: AlertView): AlertState[] {
  const states = Object.keys(ALERT_STATE_UI) as AlertState[];
  return view === 'all' ? states : states.filter((s) => ALERT_STATE_UI[s].view === view);
}

/** Prototype `SEV` labels and `.pill.s-*` classes. */
export const SEVERITY_UI = {
  0: { label: 'Low', pillClass: 's-0' },
  1: { label: 'Medium', pillClass: 's-1' },
  2: { label: 'High', pillClass: 's-2' },
} as const satisfies Record<Severity, { label: string; pillClass: string }>;

/**
 * Diagnosis labels: prototype copy for the three it shows, spec §3 wording for the rest
 * (plan Appendix B).
 */
export const DIAGNOSIS_LABELS = {
  approved_not_reflected: 'Approved change not reflected in ERP',
  no_change_order: 'No change order found',
  change_rejected: 'Change was rejected',
  change_pending: 'Change pending',
  erp_ahead_of_pdm: 'ERP ahead of PDM',
  assembly_not_in_erp: 'Assembly not yet in ERP',
} as const satisfies Record<Diagnosis, string>;

/** Spec §3 detection names. */
export const DETECTION_LABELS = {
  quantity: 'Quantity mismatch',
  configuration: 'Configuration mismatch',
  presence_missing_in_erp: 'Missing in ERP',
  presence_extra_in_erp: 'Extra in ERP',
  assembly_not_in_erp: 'Assembly not in ERP',
} as const satisfies Record<Detection, string>;

/** Spec §5.8 connector status pills. */
export const CONNECTOR_STATUS_LABELS = {
  connected: 'Connected',
  failed: 'Failed',
  not_configured: 'Not configured',
} as const satisfies Record<ConnectorStatus, string>;

/** Alerts list "View" options, as `View: <label> (n)` in the prototype. */
export const ALERT_VIEW_LABELS = {
  all: 'All alerts',
  action: 'Needs action',
  waiting: 'Waiting',
  closed: 'Closed',
} as const satisfies Record<AlertView, string>;
